import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { ApiHook } from "../../hooks/apiHook";
import TicketHeader from "../Support/TicketHeader";
import "./ReportDetails.css";

// Progress steps a report moves through. A rejected report ends on the third
// step too, but rendered in red instead of green.
const STEPS = ["pending", "under_review", "resolved"];

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

const getInitials = (text) => {
    const value = (text ?? "").trim();
    if (!value) return "?";

    const words = value.split(/\s+/).filter(Boolean);
    return words.length >= 2
        ? (words[0][0] + words[1][0]).toUpperCase()
        : value.slice(0, 2).toUpperCase();
};

const isImage = (url) =>
    IMAGE_EXTENSIONS.includes((url.split(".").pop() ?? "").toLowerCase());

const ReportDetails = () => {
    const { t } = useTranslation();
    const { trackId } = useParams();

    const details = ApiHook.CallReportDetails(trackId);
    const data = details.data;

    // ── Loading ────────────────────────────────────────────────────────────
    if (details.isLoading) {
        return (
            <>
                <div className="page_head_top">{t("report_details")}</div>
                <TicketHeader />
                <div className="rd-page">
                    <div className="rd-hero">
                        <Skeleton width={220} height={28} />
                        <Skeleton width={160} height={22} />
                    </div>
                    <div className="rd-stepper">
                        <Skeleton height={70} />
                    </div>
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="rd-card">
                                <div className="rd-card__body">
                                    <Skeleton height={20} width="60%" />
                                    <Skeleton height={100} style={{ marginTop: 16 }} />
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="rd-card">
                                <div className="rd-card__body">
                                    <Skeleton count={6} height={18} style={{ marginBottom: 10 }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── Not found ──────────────────────────────────────────────────────────
    if (!data) {
        return (
            <>
                <div className="page_head_top">{t("report_details")}</div>
                <TicketHeader />
                <div className="rd-page">
                    <div className="rd-card">
                        <div className="rd-card__body">
                            <div className="rd-empty">
                                <div className="rd-empty__icon">
                                    <i className="fa-solid fa-file-circle-question"></i>
                                </div>
                                <h3 className="rd-empty__title">{t("report_not_found")}</h3>
                                <p className="rd-empty__text">{trackId}</p>
                                <Link to="/reports" className="rd-empty__action">
                                    <i className="fa-solid fa-arrow-left"></i>
                                    {t("back_to_reports")}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── Loaded ─────────────────────────────────────────────────────────────
    const isRejected = data.status === "rejected";
    const activeIndex = isRejected ? 2 : STEPS.indexOf(data.status);
    const attachments = data.attachments ?? [];

    return (
        <>
            <div className="page_head_top">{t("report_details")}</div>
            <TicketHeader />

            <div className="rd-page">

                {/* ═══ Hero ═══════════════════════════════════════════════ */}
                <div className="rd-hero">
                    <div className="rd-hero__main">
                        <Link to="/reports" className="rd-back" title={t("back_to_reports")}>
                            <i className="fa-solid fa-arrow-left"></i>
                        </Link>

                        <div>
                            <h1 className="rd-hero__id">{data.trackId}</h1>
                        </div>

                        <div className="rd-hero__badges">
                            <span className={`rd-badge rd-badge--${data.status}`}>
                                <span className={`rd-dot rd-dot--${data.status}`}></span>
                                {t(data.status)}
                            </span>
                            <span className="rd-badge rd-badge--category">
                                <i className="fa-solid fa-tag"></i>
                                {t(data.category)}
                            </span>
                        </div>
                    </div>

                    {data.createdAt && (
                        <span className="rd-hero__date">
                            <i className="fa-regular fa-calendar"></i>
                            {data.createdAt}
                        </span>
                    )}
                </div>

                {/* ═══ Stepper ════════════════════════════════════════════ */}
                <div className="rd-stepper">
                    <p className="rd-stepper__title">{t("report_progress")}</p>
                    <div className="rd-stepper__track">
                        {STEPS.map((step, index) => {
                            const isLast = index === STEPS.length - 1;
                            const isDone = index < activeIndex;
                            const isCurrent = index === activeIndex;

                            // The final step renders as "rejected" when the report was rejected
                            const label = isLast && isRejected ? "rejected" : step;

                            const classes = [
                                "rd-step",
                                isDone ? "is-done" : "",
                                isCurrent && !(isLast && isRejected) ? "is-current" : "",
                                isCurrent && isLast && isRejected ? "is-rejected" : "",
                            ].filter(Boolean).join(" ");

                            return (
                                <div key={step} className={classes}>
                                    <div className="rd-step__circle">
                                        {isDone ? (
                                            <i className="fa-solid fa-check"></i>
                                        ) : isCurrent && isLast && isRejected ? (
                                            <i className="fa-solid fa-xmark"></i>
                                        ) : (
                                            index + 1
                                        )}
                                    </div>
                                    <span className="rd-step__label">{t(label)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ═══ Body ═══════════════════════════════════════════════ */}
                <div className="row">

                    {/* ─── Left: content + attachments ─────────────────── */}
                    <div className="col-lg-8">

                        <div className="rd-card">
                            <div className="rd-card__head">
                                <h2 className="rd-card__title">
                                    <i className="fa-regular fa-file-lines"></i>
                                    {t("report_content")}
                                </h2>
                            </div>
                            <div className="rd-card__body">
                                {data.subject && (
                                    <>
                                        <span className="rd-label">{t("subject")}</span>
                                        <p className="rd-subject">{data.subject}</p>
                                    </>
                                )}
                                {data.description && (
                                    <>
                                        <span className="rd-label">{t("report_description")}</span>
                                        <div className="rd-quote">{data.description}</div>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="rd-card">
                            <div className="rd-card__head">
                                <h2 className="rd-card__title">
                                    <i className="fa-solid fa-paperclip"></i>
                                    {t("attachment")}
                                </h2>
                                <span className="rd-card__count">{attachments.length}</span>
                            </div>
                            <div className="rd-card__body">
                                {attachments.length === 0 ? (
                                    <div className="rd-empty">
                                        <div className="rd-empty__icon">
                                            <i className="fa-regular fa-image"></i>
                                        </div>
                                        <h3 className="rd-empty__title">{t("no_attachments")}</h3>
                                    </div>
                                ) : (
                                    <div className="rd-gallery">
                                        {attachments.map((url, i) => (
                                            <a
                                                key={i}
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="rd-gallery__item"
                                            >
                                                {isImage(url) ? (
                                                    <img src={url} alt="" />
                                                ) : (
                                                    <div className="rd-gallery__file">
                                                        <i className="fa-regular fa-file"></i>
                                                        <span>{url.split("/").pop()}</span>
                                                    </div>
                                                )}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>

                    {/* ─── Right: metadata ─────────────────────────────── */}
                    <div className="col-lg-4">
                        <div className="rd-card">
                            <div className="rd-card__head">
                                <h2 className="rd-card__title">
                                    <i className="fa-solid fa-circle-info"></i>
                                    {t("report_details")}
                                </h2>
                            </div>
                            <div className="rd-card__body">

                                {data.reportedUsername && (
                                    <div className="rd-identity">
                                        <div className="rd-avatar">
                                            {getInitials(data.reportedFullName || data.reportedUsername)}
                                        </div>
                                        <div>
                                            <p className="rd-identity__name">{data.reportedUsername}</p>
                                            <p className="rd-identity__sub">
                                                {data.reportedFullName || t("reported_user")}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="rd-meta-row">
                                    <span className="rd-meta-row__key">{t("report_id")}</span>
                                    <span className="rd-meta-row__val">{data.trackId}</span>
                                </div>

                                {data.category && (
                                    <div className="rd-meta-row">
                                        <span className="rd-meta-row__key">{t("report_category")}</span>
                                        <span className="rd-meta-row__val">{t(data.category)}</span>
                                    </div>
                                )}

                                {data.status && (
                                    <div className="rd-meta-row">
                                        <span className="rd-meta-row__key">{t("report_status")}</span>
                                        <span className="rd-meta-row__val">
                                            <span className={`rd-badge rd-badge--${data.status}`}>
                                                <span className={`rd-dot rd-dot--${data.status}`}></span>
                                                {t(data.status)}
                                            </span>
                                        </span>
                                    </div>
                                )}

                                {data.createdAt && (
                                    <div className="rd-meta-row">
                                        <span className="rd-meta-row__key">{t("created_on")}</span>
                                        <span className="rd-meta-row__val">{data.createdAt}</span>
                                    </div>
                                )}

                                {data.updatedAt && (
                                    <div className="rd-meta-row">
                                        <span className="rd-meta-row__key">{t("last_updated")}</span>
                                        <span className="rd-meta-row__val">{data.updatedAt}</span>
                                    </div>
                                )}

                                {data.adminNote && (
                                    <div className="rd-meta-row rd-meta-row--stacked">
                                        <span className="rd-meta-row__key">{t("admin_note")}</span>
                                        <span className="rd-meta-row__val">{data.adminNote}</span>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default ReportDetails;
